import { LineChartUp01 } from 'blaze-ui/icons/16'
import { MessageChat01, Marker2, Bag04, UsersProfiles1, Cursor04, ThumbUp, Pointer1, EyeOpen } from 'blaze-ui/icons/20'
import Marker03 from 'blaze-ui/icons/20/Marker03'
import { Icon } from 'blaze-ui/icons/Types'
import type { BusinessType, Goal } from 'types'

export interface GoalOption {
  id: Goal
  category: string
  title: string
  description: string
  isRecommended?: boolean
  icon: Icon
  iconColor: string
  backgroundColor: string
}

export const BUSINESS_TYPE_OPTIONS: Array<{
  id: BusinessType
  title: string
  description: string
  icon: React.ComponentType<{ color?: string; size?: number }>
  iconColor: string
  backgroundColor: string
}> = [
  {
    id: 'service',
    title: 'Services',
    description: 'Consultant, agencies, freelancer',
    icon: MessageChat01,
    iconColor: 'var(--green)',
    backgroundColor: 'var(--green-10)',
  },
  {
    id: 'place',
    title: 'Local Businesses',
    description: 'Restaurants, stores, salons',
    icon: Marker2,
    iconColor: 'var(--starter)',
    backgroundColor: 'var(--starter-10)',
  },
  {
    id: 'product',
    title: 'Products',
    description: 'E-commerce, manufacturers',
    icon: Bag04,
    iconColor: 'var(--upgrade)',
    backgroundColor: 'var(--upgrade-10)',
  },
]

export const GOAL_OPTIONS_BY_BUSINESS_TYPE: Record<BusinessType, GoalOption[]> = {
  service: [
    {
      id: 'grow_audience',
      category: 'Awareness',
      title: 'Grow Your Audience',
      description: "Reach new potential clients who haven't discovered you yet",
      isRecommended: true,
      icon: LineChartUp01,
      iconColor: 'var(--starter)',
      backgroundColor: 'var(--starter-10)',
    },
    {
      id: 'generate_leads',
      category: 'Consideration',
      title: 'Generate Leads',
      description: "Help prospects understand what you do and why you're the right fit",
      icon: UsersProfiles1,
      iconColor: 'var(--growth)',
      backgroundColor: 'var(--growth-10)',
    },
    {
      id: 'convert_clients',
      category: 'Conversion',
      title: 'Convert Clients',
      description: 'Turn interested prospects into paying clients.',
      icon: Cursor04,
      iconColor: 'rgba(217, 49, 239, 1)',
      backgroundColor: 'rgba(217, 49, 239, 0.1)',
    },
    {
      id: 'drive_repeat_business',
      category: 'Expansion',
      title: 'Drive Repeat Business',
      description: 'Keep past clients coming back and referring others to you.',
      icon: ThumbUp,
      iconColor: 'var(--upgrade)',
      backgroundColor: 'var(--upgrade-10)',
    },
  ],
  place: [
    {
      id: 'get_found_locally',
      category: 'Awareness',
      title: 'Get Found Locally',
      description: "Reach people nearby who need what you offer but haven't found you yet",
      isRecommended: true,
      icon: Marker03,
      iconColor: 'var(--starter)',
      backgroundColor: 'var(--starter-10)',
    },
    {
      id: 'bring_in_new_customers',
      category: 'Consideration',
      title: 'Bring In New Customers',
      description: 'Get people to visit, book, or reach out for the first time',
      icon: Bag04,
      iconColor: 'var(--growth)',
      backgroundColor: 'var(--growth-10)',
    },
    {
      id: 'keep_customers_coming_back',
      category: 'Conversion',
      title: 'Keep Customers Coming Back',
      description: 'Turn first-time visitors into regulars.',
      icon: Pointer1,
      iconColor: 'rgba(217, 49, 239, 1)',
      backgroundColor: 'rgba(217, 49, 239, 0.1)',
    },
    {
      id: 'grow_through_word_of_mouth',
      category: 'Expansion',
      title: 'Grow Through Word-of-Mouth',
      description: 'Turn happy customers into advocates who spread the word.',
      icon: MessageChat01,
      iconColor: 'var(--upgrade)',
      backgroundColor: 'var(--upgrade-10)',
    },
  ],
  product: [
    {
      id: 'grow_audience',
      category: 'Awareness',
      title: 'Grow Your Audience',
      description: "Reach shoppers who haven't discovered your brand yet",
      isRecommended: true,
      icon: LineChartUp01,
      iconColor: 'var(--starter)',
      backgroundColor: 'var(--starter-10)',
    },
    {
      id: 'build_product_awareness',
      category: 'Consideration',
      title: 'Build Awareness',
      description: 'Show shoppers why your products are worth buying.',
      icon: EyeOpen,
      iconColor: 'var(--growth)',
      backgroundColor: 'var(--growth-10)',
    },
    {
      id: 'make_more_sales',
      category: 'Conversion',
      title: 'Make More Sales',
      description: 'Turn browsers into buyers with the right offer at the right time.',
      icon: Cursor04,
      iconColor: 'rgba(217, 49, 239, 1)',
      backgroundColor: 'rgba(217, 49, 239, 0.1)',
    },
    {
      id: 'create_repeat_customers',
      category: 'Expansion',
      title: 'Create Repeat Customers',
      description: 'Turn one-time buyers into loyal customers.',
      icon: ThumbUp,
      iconColor: 'var(--upgrade)',
      backgroundColor: 'var(--upgrade-10)',
    },
  ],
}

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  service: 'service-based',
  place: 'place-based',
  product: 'product-based',
}
